import { useEffect, useState } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import Modal from "../../../components/ui/Modal";
import Skeleton from "../../../components/ui/Skeleton";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import NumberInput from "../../../components/ui/NumberInput";
import FileUpload from "../../../components/ui/FileUpload";
import StatusBadge from "../../../components/ui/StatusBadge";
import { EmptyState } from "../../../components/ui/FeedbackStates";
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import masterService from "../../../services/masterEntitiesService";
import { resolveBackendUrl } from "../../../services/api";
import { toast } from "react-toastify";
import { Plus, Edit2, Trash2, Car, Image as ImageIcon } from "lucide-react";
import styles from "../Masters.module.css";

export default function MasterCabProviders() {
  const [cabs, setCabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCab, setEditingCab] = useState(null);
  const [deletingCab, setDeletingCab] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [name, setName] = useState("");
  const [colorCode, setColorCode] = useState("#000000");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  const loadCabs = async () => {
    setLoading(true);
    try {
      const data = await masterService.getCabProviders();
      setCabs(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load cab providers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCabs();
  }, []);

  const openAddModal = () => {
    setEditingCab(null);
    setName("");
    setColorCode("#000000");
    setDisplayOrder(cabs.length + 1);
    setLogoFile(null);
    setLogoPreview("");
    setModalOpen(true);
  };

  const openEditModal = (cab) => {
    setEditingCab(cab);
    setName(cab.name);
    setColorCode(cab.color_code || "#000000");
    setDisplayOrder(cab.display_order || 0);
    setLogoFile(null);
    setLogoPreview(cab.logo_url ? resolveBackendUrl(cab.logo_url) : "");
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Cab provider name is required.");
      return;
    }

    if (!editingCab && !logoFile) {
      toast.error("Logo file is required for new cab providers.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("color_code", colorCode);
      formData.append("display_order", displayOrder);
      if (logoFile) {
        formData.append("logo", logoFile);
      }

      if (editingCab) {
        await masterService.updateCabProvider(editingCab.id, formData);
        toast.success("Cab provider updated successfully!");
      } else {
        await masterService.createCabProvider(formData);
        toast.success("Cab provider created successfully!");
      }

      setModalOpen(false);
      loadCabs();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to save cab provider.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (cab) => {
    try {
      await masterService.updateCabProvider(cab.id, { is_active: !cab.is_active });
      toast.success(`${cab.name} is now ${!cab.is_active ? "Active" : "Inactive"}`);
      loadCabs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to toggle status.");
    }
  };

  return (
    <AdminLayout title="Master Cab Providers">
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              <Car size={24} color="#0f172a" />
              Master Cab Providers
            </h1>
            <p className={styles.subtitle}>
              Global catalog of ride-hailing services (Uber, Ola, BluSmart, InDrive) with official branding logos.
            </p>
          </div>
          <Button variant="primary" onClick={openAddModal}>
            <Plus size={16} style={{ marginRight: "4px" }} /> Add Cab Provider
          </Button>
        </div>

        {loading ? (
          <div style={{ padding: "24px" }}>
            <Skeleton variant="card" count={3} />
          </div>
        ) : cabs.length === 0 ? (
          <EmptyState
            title="No cab providers found"
            description="Add your first cab provider to enable pre-approved taxi gate entry passes."
            action={
              <Button onClick={openAddModal}>
                <Plus size={15} style={{ marginRight: "4px" }} /> Add Cab Provider
              </Button>
            }
          />
        ) : (
          <div className={styles.grid}>
            {cabs.map((cab) => (
              <div key={cab.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <div className={styles.logoWrap}>
                    {cab.logo_url ? (
                      <img
                        src={resolveBackendUrl(cab.logo_url)}
                        alt={cab.name}
                        className={styles.logoImg}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <ImageIcon size={24} color="#94a3b8" />
                    )}
                  </div>
                  <div className={styles.cardInfo}>
                    <div className={styles.companyName}>
                      {cab.name}
                      {cab.color_code && (
                        <span
                          style={{
                            display: "inline-block",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            marginLeft: "6px",
                            backgroundColor: cab.color_code,
                          }}
                        />
                      )}
                    </div>
                    <span className={styles.categoryBadge}>CAB_AGGREGATOR</span>
                  </div>
                </div>

                <div className={styles.cardBottom} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                  <button
                    type="button"
                    className={styles.statusBadgeBtn}
                    onClick={() => toggleStatus(cab)}
                    title={`Click to ${cab.is_active ? "deactivate" : "activate"}`}
                  >
                    <StatusBadge
                      status={cab.is_active ? "ACTIVE" : "INACTIVE"}
                      label={cab.is_active ? "Active" : "Inactive"}
                    />
                  </button>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <Button variant="outline" size="sm" onClick={() => openEditModal(cab)}>
                      <Edit2 size={13} />
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => setDeletingCab(cab)}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={!!deletingCab}
          onClose={() => setDeletingCab(null)}
          entityType="Cab Provider"
          entityId={deletingCab?.id}
          entityName={deletingCab?.name}
          canDelete={true}
          onConfirm={async () => {
            try {
              await masterService.deleteCabProvider(deletingCab.id);
              toast.success("Cab provider deleted successfully!");
              setDeletingCab(null);
              loadCabs();
            } catch (err) {
              console.error(err);
              toast.error(err.message || "Failed to delete cab provider.");
            }
          }}
        />

        {/* Create / Edit Modal */}
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingCab ? `Edit ${editingCab.name}` : "Add Master Cab Provider"}
        >
          <form onSubmit={handleSubmit} className={styles.form} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <FormField label="Provider Name" required>
              <TextInput
                required
                placeholder="e.g. Uber, Ola Cabs, BluSmart, Rapido"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <FormField label="Brand Color Hex">
                <TextInput
                  value={colorCode}
                  onChange={(e) => setColorCode(e.target.value)}
                  placeholder="#000000"
                  leadIcon={
                    <span
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "3px",
                        backgroundColor: colorCode || "#000000",
                        display: "inline-block",
                        border: "1px solid var(--color-border)",
                      }}
                    />
                  }
                />
              </FormField>

              <FormField label="Display Order">
                <NumberInput
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(Number(e.target.value) || 0)}
                  min={0}
                  max={999}
                />
              </FormField>
            </div>

            <FormField
              label="Provider Brand Logo"
              required={!editingCab}
              helperText="PNG, WebP, or JPG under 500 KB"
            >
              <FileUpload
                accept="image/png,image/webp,image/jpeg"
                maxSizeMB={0.5}
                value={logoPreview}
                helperText="Upload official brand logo"
                onChange={(file, dataUrl) => {
                  setLogoFile(file);
                  setLogoPreview(dataUrl);
                }}
              />
            </FormField>

            <div className={styles.formActions} style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
              <Button variant="outline" onClick={() => setModalOpen(false)} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting} loading={submitting}>
                {editingCab ? "Save Changes" : "Create Provider"}
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </AdminLayout>
  );
}
